/**
 * Quanto detalhe cabe no gráfico do Ritmo.
 *
 * A janela vai de uma semana a um trimestre e a tela vai de 390px a um
 * monitor: as duas juntas decidem o que dá para desenhar. Com uma semana no
 * desktop sobra espaço para partir o dia em três colunas e ainda escrever o
 * total em cima de cada uma; com um trimestre no celular nem as datas do eixo
 * cabem todas.
 *
 * A conta mora aqui, e não dentro do componente, porque ela erra calada: o
 * rótulo que não cabe não some, ele encavala no vizinho, e ninguém abre um
 * painel para conferir se o eixo do gráfico está legível.
 */

/**
 * O tamanho da coluna, em horas, por janela.
 *
 * **A coluna do gráfico não precisa ser o dia.** Uma semana em dia dá sete
 * colunas num gráfico que comporta trinta: o desenho fica esparso sem que falte
 * dado - o que falta é resolução. Partindo o dia, a mesma semana rende vinte e
 * quatro colunas, e aparece o que o dia escondia, que é a hora em que o
 * trabalho acontece.
 *
 * Os blocos foram escolhidos para as três janelas curtas caírem todas na mesma
 * faixa de vinte e poucas colunas, que é onde o gráfico fica cheio sem ficar
 * apertado. Todos dividem 24 - a rota recusa o resto, porque bloco que
 * atravessa a meia-noite faz uma coluna pertencer a dois dias.
 *
 * **No celular o dia continua sendo a coluna.** Lá o vão não sobra: sete dias
 * em 300px já dão barra de 14px, e parti-la em três daria 4px. O gráfico
 * esparso é problema de tela larga.
 */
export function blocoDoRitmo(dias: number, compacto: boolean): number {
  if (compacto) return 24;
  if (dias <= 7) return 8;
  if (dias <= 14) return 12;
  return 24;
}

/**
 * O bloco que a **consulta** pede: o mais fino que alguma tela desenha.
 *
 * Pedir o bloco que esta tela desenha faria a largura da janela do navegador
 * virar ida ao servidor - e não uma: o `useBreakpoint` do Antd só sabe o
 * tamanho depois da primeira pintura, então o desktop buscaria a série em dia,
 * descobriria que é desktop e buscaria de novo em bloco. Pedindo sempre o mais
 * fino, o celular recolhe os blocos no desenho (`serieDoRitmo`) e atravessar o
 * `md` de um lado para o outro não custa nada. O que vem a mais é uma linha por
 * bloco em vez de por dia, com o mesmo total.
 */
export const blocoDaConsulta = (dias: number): number => blocoDoRitmo(dias, false);

/** Quantas datas o eixo aguenta antes de virar um borrão cinza. */
const DATAS_NO_EIXO = { compacto: 5, largo: 12 };

/**
 * Janelas em que o número cabe escrito em cima da coluna.
 *
 * O corte é pela **janela**, e não pelo número de colunas, porque as duas
 * coisas que decidem se o número cabe andam juntas com ela: o vão entre colunas
 * e quantos dígitos o número tem. As três janelas curtas ficam entre 22 e 30
 * colunas - vão de 22px no desktop mais estreito que existe (768px) -, e o
 * bloco menor faz o número encolher junto: peça de meio dia raramente passa de
 * dois dígitos, que ocupam 12px. O mês e o trimestre continuam em coluna de
 * dia, com três dígitos possíveis em vão de 21px, e ali o número encavalaria.
 *
 * No celular, onde a coluna é sempre o dia, só a semana passa.
 */
const JANELA_COM_NUMERO = { compacto: 7, largo: 21 };

/**
 * Até quantas colunas os fios de 1px separam mais do que atrapalham.
 *
 * O fio da cor do cartão entre as faixas da pilha e o vão entre a barra das
 * peças e a das versões são detalhe de acabamento, e detalhe de 1px come a
 * barra quando a barra tem 1px. Era o que acontecia no trimestre visto no
 * celular: 91 dias em 300px dão barra de 1.17px, o traçado cobria os dois
 * lados dela e o gráfico ficava **em branco** - não "sem trabalho", em branco,
 * que é a pior forma de errar num painel.
 *
 * A barra fica com uns 45% do vão da coluna, e o fio precisa que sobre uns 3px
 * dela. Um monitor estreito aguenta o trimestre; 300px de celular não passam de
 * um mês e meio.
 */
const COLUNAS_COM_FIO = { compacto: 45, largo: 99 };

export interface DensidadeDoRitmo {
  /** O `interval` do eixo do Recharts: desenha uma data e pula estas. */
  intervaloDoEixo: number;
  /** Se o total de peças da coluna cabe escrito em cima dela. */
  numeroNaColuna: boolean;
  /** Se a barra é larga o bastante para carregar os fios de 1px. */
  fiosDeSeparacao: boolean;
}

export function densidadeDoRitmo({
  dias,
  colunas,
  bloco,
  compacto,
}: {
  /** A janela pedida, em dias. */
  dias: number;
  /** Colunas que a série tem, já contando os blocos e os dias parados. */
  colunas: number;
  /** Horas por coluna, de `blocoDoRitmo`. */
  bloco: number;
  compacto: boolean;
}): DensidadeDoRitmo {
  const tela = compacto ? 'compacto' : 'largo';
  const colunasPorDia = 24 / bloco;

  /*
   * O eixo mostra datas, e não blocos: "22/08 08h" em coluna de 20px seria
   * ilegível, e repetir a data em cada bloco do mesmo dia seria pior. Por isso
   * o passo é contado em **dias** e depois convertido em colunas - assim a marca
   * cai sempre no primeiro bloco do dia, que é onde a data significa o que diz.
   * Com a coluna valendo um dia, a conta é a de sempre.
   */
  const passoEmDias = Math.max(1, Math.ceil(colunas / colunasPorDia / DATAS_NO_EIXO[tela]));

  return {
    intervaloDoEixo: colunas === 0 ? 0 : passoEmDias * colunasPorDia - 1,
    numeroNaColuna: colunas > 0 && dias <= JANELA_COM_NUMERO[tela],
    fiosDeSeparacao: colunas <= COLUNAS_COM_FIO[tela],
  };
}
